import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { Clock, CheckCircle, FileUp, MapPin, MessageSquare, AlertCircle, Check, Calendar as CalendarIcon, Plus, X, Save } from 'lucide-react';
import LocationMap from '../components/LocationMap';
import OrderNotes from '../components/OrderNotes';

interface Auftrag {
  id: string;
  auftragsnummer: string;
  internal_number: string;
  auftragsbezeichnung: string;
  auftragsdatum: string;
  status: 'neu' | 'begonnen' | 'abgeschlossen' | 'abgerechnet';
  begonnen_am: string | null;
  abgeschlossen_am: string | null;
  kunde_id: string;
  standort_id: string;
  pdf_name: string | null;
  pdf_data: string | null;
}

interface Kunde {
  id: string;
  name: string;
}

interface Standort {
  id: string;
  name: string;
  adresse: string;
}

interface CompletionConfirmation {
  show: boolean;
  auftragId: string | null;
  isConfirmed: boolean;
  hasWorksheet: boolean;
}

interface TerminFormData {
  title: string;
  description: string;
  start_time: string;
  end_time: string;
}

function MonteurDashboard() {
  const { monteurId } = useAuth();
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuftrag, setSelectedAuftrag] = useState<Auftrag | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [completionConfirm, setCompletionConfirm] = useState<CompletionConfirmation>({
    show: false,
    auftragId: null,
    isConfirmed: false,
    hasWorksheet: false,
  });
  const [showTerminForm, setShowTerminForm] = useState(false);
  const [terminFormData, setTerminFormData] = useState<TerminFormData>({
    title: '',
    description: '',
    start_time: new Date().toISOString().split('.')[0],
    end_time: new Date(Date.now() + 60 * 60 * 1000).toISOString().split('.')[0],
  });

  useEffect(() => {
    if (monteurId) {
      loadData();
    }
  }, [monteurId]);

  async function loadData() {
    try {
      setLoading(true);
      
      // Fetch all necessary data
      const [auftraegeRes, kundenRes, standorteRes] = await Promise.all([
        supabase
          .from('auftraege')
          .select('*')
          .eq('monteur_id', monteurId)
          .in('status', ['neu', 'begonnen'])
          .order('auftragsdatum', { ascending: false }),
        supabase.from('kunden').select('id, name'),
        supabase.from('standorte').select('id, name, adresse')
      ]);

      if (auftraegeRes.error) throw auftraegeRes.error;
      if (kundenRes.error) throw kundenRes.error;
      if (standorteRes.error) throw standorteRes.error;

      setAuftraege(auftraegeRes.data || []);
      setKunden(kundenRes.data || []);
      setStandorte(standorteRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }

  const handleTerminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAuftrag || !monteurId) return;

    try {
      const { error } = await supabase
        .from('termine')
        .insert([{
          title: terminFormData.title,
          description: terminFormData.description,
          start_time: terminFormData.start_time,
          end_time: terminFormData.end_time,
          monteur_id: monteurId,
          auftrag_id: selectedAuftrag.id,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (error) throw error;

      setShowTerminForm(false);
      setTerminFormData({
        title: '',
        description: '',
        start_time: new Date().toISOString().split('.')[0],
        end_time: new Date(Date.now() + 60 * 60 * 1000).toISOString().split('.')[0],
      });

      alert('Termin wurde erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert('Fehler beim Erstellen des Termins');
    }
  };

  const handleTerminInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTerminFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updateAuftragStatus = async (auftragId: string, newStatus: Auftrag['status']) => {
    try {
      setStatusUpdateError(null);
      const auftrag = auftraege.find(a => a.id === auftragId);
      if (!auftrag) return;

      // For completion, show confirmation dialog
      if (newStatus === 'abgeschlossen') {
        setCompletionConfirm({
          show: true,
          auftragId,
          isConfirmed: false,
          hasWorksheet: false,
        });
        return;
      }

      // For other status changes, proceed directly
      await performStatusUpdate(auftragId, newStatus);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setStatusUpdateError('Fehler beim Aktualisieren des Status. Bitte versuchen Sie es erneut.');
    }
  };

  const performStatusUpdate = async (auftragId: string, newStatus: Auftrag['status']) => {
    try {
      setStatusUpdateError(null);
      const now = new Date().toISOString();
      const updates: Record<string, any> = {
        status: newStatus,
      };

      // Set the appropriate timestamp based on status
      if (newStatus === 'begonnen') {
        updates.begonnen_am = now;
      } else if (newStatus === 'abgeschlossen') {
        updates.abgeschlossen_am = now;
      }

      const { data, error } = await supabase
        .from('auftraege')
        .update(updates)
        .eq('id', auftragId)
        .select();

      if (error) throw error;

      // Update local state with the updated data
      if (data && data.length > 0) {
        const updatedAuftrag = data[0];
        
        // Update auftraege list
        setAuftraege(prev => 
          prev.map(auftrag => 
            auftrag.id === auftragId ? updatedAuftrag : auftrag
          )
        );
        
        // Update selected auftrag if it's the one being modified
        if (selectedAuftrag && selectedAuftrag.id === auftragId) {
          setSelectedAuftrag(updatedAuftrag);
        }
      }

      // If we're completing an order, remove it from the selected auftrag
      if (newStatus === 'abgeschlossen' && selectedAuftrag?.id === auftragId) {
        setSelectedAuftrag(null);
      }

      // Reset completion confirmation
      setCompletionConfirm({
        show: false,
        auftragId: null,
        isConfirmed: false,
        hasWorksheet: false,
      });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setStatusUpdateError('Fehler beim Aktualisieren des Status. Bitte versuchen Sie es erneut.');
    }
  };

  const handleCompletionConfirm = () => {
    if (!completionConfirm.auftragId) return;
    
    // Check if both checkboxes are checked
    if (!completionConfirm.isConfirmed || !completionConfirm.hasWorksheet) {
      setStatusUpdateError('Bitte bestätigen Sie beide Checkboxen, bevor Sie den Auftrag abschließen.');
      return;
    }
    
    // Proceed with status update
    performStatusUpdate(completionConfirm.auftragId, 'abgeschlossen');
  };

  const cancelCompletionConfirm = () => {
    setCompletionConfirm({
      show: false,
      auftragId: null,
      isConfirmed: false,
      hasWorksheet: false,
    });
    setStatusUpdateError(null);
  };

  const openPDF = (pdfData: string | undefined) => {
    if (pdfData) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <iframe
            src="${pdfData}"
            style="width:100%; height:100vh; border:none;"
          ></iframe>
        `);
      }
    }
  };

  const getStatusColor = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'bg-blue-100 text-blue-800';
      case 'begonnen':
        return 'bg-yellow-100 text-yellow-800';
      case 'abgeschlossen':
        return 'bg-green-100 text-green-800';
      case 'abgerechnet':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'Neu';
      case 'begonnen':
        return 'In Bearbeitung';
      case 'abgeschlossen':
        return 'Abgeschlossen';
      case 'abgerechnet':
        return 'Abgerechnet';
      default:
        return status;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  const neueAuftraege = auftraege.filter(a => a.status === 'neu');
  const laufendeAuftraege = auftraege.filter(a => a.status === 'begonnen');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Meine Aufträge</h1>
        
        {statusUpdateError && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
            {statusUpdateError}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column - List of orders */}
          <div className="space-y-6">
            {/* New orders */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Clock className="text-blue-600" size={20} />
                Neue Aufträge ({neueAuftraege.length})
              </h2>
              
              {neueAuftraege.length > 0 ? (
                <div className="space-y-3">
                  {neueAuftraege.map(auftrag => {
                    const kunde = kunden.find(k => k.id === auftrag.kunde_id);
                    const standort = standorte.find(s => s.id === auftrag.standort_id);
                    
                    return (
                      <div 
                        key={auftrag.id} 
                        className={`border rounded-lg p-3 cursor-pointer hover:bg-gray-50 ${
                          selectedAuftrag?.id === auftrag.id ? 'border-blue-500 bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedAuftrag(auftrag)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium">{standort?.name}</h3>
                            <p className="text-sm text-gray-600">{auftrag.internal_number}</p>
                            <div className="mt-1 text-sm text-gray-500">
                              <p>{kunde?.name}</p>
                              <p>{standort?.adresse}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(auftrag.status)}`}>
                            {getStatusLabel(auftrag.status)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">Keine neuen Aufträge vorhanden</p>
              )}
            </div>
            
            {/* In progress orders */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Clock className="text-yellow-600" size={20} />
                Aufträge in Bearbeitung ({laufendeAuftraege.length})
              </h2>
              
              {laufendeAuftraege.length > 0 ? (
                <div className="space-y-3">
                  {laufendeAuftraege.map(auftrag => {
                    const kunde = kunden.find(k => k.id === auftrag.kunde_id);
                    const standort = standorte.find(s => s.id === auftrag.standort_id);
                    
                    return (
                      <div 
                        key={auftrag.id} 
                        className={`border rounded-lg p-3 cursor-pointer hover:bg-gray-50 ${
                          selectedAuftrag?.id === auftrag.id ? 'border-blue-500 bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedAuftrag(auftrag)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium">{standort?.name}</h3>
                            <p className="text-sm text-gray-600">{auftrag.internal_number}</p>
                            <div className="mt-1 text-sm text-gray-500">
                              <p>{kunde?.name}</p>
                              <p>{standort?.adresse}</p>
                              {auftrag.begonnen_am && (
                                <p>In Bearbeitung seit: {new Date(auftrag.begonnen_am).toLocaleDateString()}</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(auftrag.status)}`}>
                            {getStatusLabel(auftrag.status)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">Keine Aufträge in Bearbeitung</p>
              )}
            </div>
          </div>
          
          {/* Right column - Selected order details */}
          <div>
            {selectedAuftrag ? (
              <div className="space-y-4">
                <div className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-xl font-semibold">{(() => {
                        const standort = standorte.find(s => s.id === selectedAuftrag.standort_id);
                        return standort?.name || 'Unbekannter Standort';
                      })()}</h2>
                      <p className="text-gray-600">{selectedAuftrag.internal_number}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedAuftrag.status)}`}>
                      {getStatusLabel(selectedAuftrag.status)}
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Order details in specified order */}
                    <div className="space-y-2">
                      {(() => {
                        const kunde = kunden.find(k => k.id === selectedAuftrag.kunde_id);
                        const standort = standorte.find(s => s.id === selectedAuftrag.standort_id);
                        
                        return (
                          <>
                            <p><strong>Datum:</strong> {new Date(selectedAuftrag.auftragsdatum).toLocaleDateString()}</p>
                            <p><strong>Kunde:</strong> {kunde?.name}</p>
                            <p><strong>Standort:</strong> {standort?.name}</p>
                            <p><strong>Adresse:</strong> {standort?.adresse}</p>
                            <p><strong>Bestellnummer:</strong> {selectedAuftrag.internal_number}</p>
                            <p><strong>Auftragsnummer:</strong> {selectedAuftrag.auftragsnummer}</p>
                            <p><strong>Beschreibung:</strong> {selectedAuftrag.auftragsbezeichnung}</p>
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Map toggle */}
                    <div>
                      <button
                        onClick={() => setShowMap(!showMap)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                      >
                        <MapPin size={16} />
                        {showMap ? 'Karte ausblenden' : 'Karte anzeigen'}
                      </button>
                      
                      {showMap && (() => {
                        const standort = standorte.find(s => s.id === selectedAuftrag.standort_id);
                        if (standort) {
                          return (
                            <div className="mt-2">
                              <LocationMap address={standort.adresse} name={standort.name} />
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    
                    {/* Notes toggle */}
                    <div>
                      <button
                        onClick={() => setShowNotes(!showNotes)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                      >
                        <MessageSquare size={16} />
                        {showNotes ? 'Notizen ausblenden' : 'Notizen anzeigen'}
                      </button>
                    </div>

                    {/* Calendar button */}
                    <div>
                      <button
                        onClick={() => setShowTerminForm(true)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                      >
                        <CalendarIcon size={16} />
                        Termin hinzufügen
                      </button>
                    </div>
                    
                    {/* PDF button */}
                    {selectedAuftrag.pdf_data && (
                      <div>
                        <button
                          onClick={() => openPDF(selectedAuftrag.pdf_data || undefined)}
                          className="w-full bg-blue-100 text-blue-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-blue-200"
                        >
                          <FileUp size={16} />
                          PDF Auftrag öffnen
                          {selectedAuftrag.pdf_name && ` (${selectedAuftrag.pdf_name})`}
                        </button>
                      </div>
                    )}
                    
                    {/* Action buttons */}
                    <div className="space-y-2 pt-2">
                      {selectedAuftrag.status === 'neu' && (
                        <button
                          onClick={() => updateAuftragStatus(selectedAuftrag.id, 'begonnen')}
                          className="w-full bg-yellow-100 text-yellow-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-yellow-200"
                        >
                          <Clock size={16} />
                          In Bearbeitung setzen
                        </button>
                      )}
                      
                      {selectedAuftrag.status === 'begonnen' && (
                        <button
                          onClick={() => updateAuftragStatus(selectedAuftrag.id, 'abgeschlossen')}
                          className="w-full bg-green-100 text-green-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-green-200"
                        >
                          <CheckCircle size={16} />
                          Als abgeschlossen markieren
                        </button>
                      )}
                    </div>
                    
                    {/* Timestamps */}
                    <div className="text-xs text-gray-500 space-y-1 pt-2 border-t">
                      {selectedAuftrag.begonnen_am && (
                        <p>In Bearbeitung seit: {new Date(selectedAuftrag.begonnen_am).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Notes section */}
                {showNotes && (
                  <OrderNotes auftragId={selectedAuftrag.id} />
                )}
              </div>
            ) : (
              <div className="border rounded-lg p-8 bg-gray-50 flex flex-col items-center justify-center h-full">
                <p className="text-gray-500 text-center mb-2">Wählen Sie einen Auftrag aus der Liste aus, um Details zu sehen</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Completion confirmation dialog */}
      {completionConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertCircle className="text-yellow-600" size={24} />
              Auftrag abschließen
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-md hover:bg-gray-50">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="confirmComplete"
                    checked={completionConfirm.isConfirmed}
                    onChange={(e) => setCompletionConfirm(prev => ({
                      ...prev,
                      isConfirmed: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <label htmlFor="confirmComplete" className="cursor-pointer">
                  <div className="font-medium">Auftrag abschließen bestätigen</div>
                  <div className="text-sm text-gray-500">
                    Ich bestätige, dass alle Arbeiten vollständig und ordnungsgemäß ausgeführt wurden.
                  </div>
                </label>
              </div>
              
              <div className="flex items-start gap-2 p-3 rounded-md hover:bg-gray-50">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="confirmWorksheet"
                    checked={completionConfirm.hasWorksheet}
                    onChange={(e) => setCompletionConfirm(prev => ({
                      ...prev,
                      hasWorksheet: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <label htmlFor="confirmWorksheet" className="cursor-pointer">
                  <div className="font-medium">Arbeitszettel ausgefüllt</div>
                  <div className="text-sm text-gray-500">
                    Ich bestätige, dass ich den Arbeitszettel vollständig ausgefüllt habe.
                  </div>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={cancelCompletionConfirm}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCompletionConfirm}
                disabled={!completionConfirm.isConfirmed || !completionConfirm.hasWorksheet}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Check size={16} />
                Auftrag abschließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar form modal */}
      {showTerminForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CalendarIcon className="text-blue-600" />
              Termin hinzufügen
            </h2>

            <form onSubmit={handleTerminSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Titel
                </label>
                <input
                  type="text"
                  name="title"
                  value={terminFormData.title}
                  onChange={handleTerminInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. Wartung durchführen"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Startzeit
                  </label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={terminFormData.start_time}
                    onChange={handleTerminInputChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Endzeit
                  </label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={terminFormData.end_time}
                    onChange={handleTerminInputChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Beschreibung (optional)
                </label>
                <textarea
                  name="description"
                  value={terminFormData.description}
                  onChange={handleTerminInputChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Zusätzliche Informationen zum Termin"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTerminForm(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
                >
                  <X size={20} />
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save size={20} />
                  Termin speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonteurDashboard;